import { cancelPayPinpad, healthPinpad, payPinpad } from '~/services/pinpad';
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import toast from 'react-hot-toast';
import api2 from '~/services/api2';

function updateCurrentPaymentFlow(pinpadPaymentStatus, pinpadHealthStatus) {
  if (pinpadPaymentStatus === 'error' || pinpadHealthStatus === 'error') {
    return 'error';
  }

  if (pinpadPaymentStatus === 'success' && pinpadHealthStatus === 'success') {
    return 'success';
  }

  if (pinpadPaymentStatus === 'loading' || pinpadHealthStatus === 'loading') {
    return 'loading';
  }

  if (pinpadPaymentStatus === 'unsaved' || pinpadHealthStatus === 'loading') {
    return 'unsaved';
  }

  return 'idle';
}

export default function () {
  const [pinpadPaymentStatus, setPinpadPaymentStatus] = useState('idle');
  const [pinpadPaymentError, setPinpadPaymentError] = useState(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [isLeavingPayment, setIsLeavingPayment] = useState(false);
  const [isChoosePinpad, setIsChoosePinpad] = useState(false);
  const [paymentPayload, setPaymentPayload] = useState({
    amount: 0,
    paymentMethod: '',
  });

  function updatePinpadPaymentError(state) {
    setPinpadPaymentError(state);
  }

  function handleChoosePayWithPinpad(value) {
    setIsChoosePinpad(value);
  }

  function updateCurrentStatus(state) {
    setPinpadPaymentStatus(state);
  }

  const { data: currentTable } = useQuery({
    queryFn: async () => {
      const { data } = await api2.get('/restaurants/tables?is_balcony=true');
      const table = data.find((tb) => tb.table_type === 'balcony');
      return table;
    },
  });

  const {
    data: balconySession,
    mutateAsync: mutateBalconySession,
    status: sessionRequestStatus,
  } = useMutation({
    mutationFn: async (id) =>
      api2
        .get(`restaurants/balcony-current-session/${id}`)
        .then((res) => res.data),
  });

  const handleGetBalconySession = () => {
    if (window.location.pathname === '/balcony') {
      mutateBalconySession(currentTable?.id);
    }
  };

  useEffect(() => {
    if (currentTable !== undefined && currentTable?.id) {
      handleGetBalconySession();
    }
  }, [currentTable]);

  const { mutateAsync: payPinpadAsync, reset: payPinpadReset } = useMutation({
    mutationFn: (data) => {
      payPinpad(data, updateCurrentStatus, updatePinpadPaymentError);
    },
    onSettled() {
      setIsLoadingPayment(false);
    },
  });

  const {
    mutateAsync: healthPinpadAsync,
    status: pinpadHealthStatus,
    reset: pinpadHealthReset,
  } = useMutation({
    mutationFn: healthPinpad,
    onSuccess(_, variables) {
      payPinpadAsync(variables);
    },
    onError(error) {
      setIsLeavingPayment(false);
      setIsLoadingPayment(false);

      if (error instanceof Error) {
        if (error.message === 'manualAbort') {
        } else if (error.message === 'timeAbort') {
          toast.error('Tempo para pagamento expirou. Tente novamente.');
        } else {
          toast.error(
            'Erro de conexão com o Pinpad. Tente novamente mais tarde.'
          );
        }
      }
    },
  });

  function handleResetPinpadOperation() {
    setPinpadPaymentStatus('idle');
    pinpadHealthReset();
    payPinpadReset();
    setPaymentPayload({
      amount: 0,
      paymentMethod: '',
    });
  }

  const { mutateAsync: savePaymentAsync } = useMutation({
    mutationFn: (payload) => {
      return api2.post('/restaurants/payments', payload);
    },
    onSuccess() {
      toast.success('Pagamento feito com sucesso.');
    },
  });

  const { mutateAsync: cancelPinpadAsync } = useMutation({
    mutationFn: cancelPayPinpad,
    onSettled() {
      handleResetPinpadOperation();
      handleSwitchPinpadCancelStatus(false);
    },
  });

  function handleStartPinpadPayment(payload) {
    if (paymentPayload.amount === 0 && paymentPayload.paymentMethod === '') {
      setPaymentPayload(payload);
      healthPinpadAsync(payload);
    } else {
      healthPinpadAsync(paymentPayload);
    }

    setIsLoadingPayment(true);
  }

  function handleSwitchPinpadCancelStatus(status) {
    setIsLeavingPayment(status);
  }

  async function handleCancelPinpadPayment() {
    cancelPinpadAsync();
  }

  useEffect(() => {
    if (pinpadPaymentError) {
      if (pinpadPaymentError === 'unsaved') {
        toast.error(
          'pagamento feito, mas não salvo no sistema. Insira o pagamento manualmente'
        );
      } else {
        handleResetPinpadOperation();
        setIsLeavingPayment(false);

        if (pinpadPaymentError === 'manualAbort') {
        } else if (pinpadPaymentError === 'abortPinpad') {
        } else if (pinpadPaymentError === 'timeAbort') {
          toast.error('Tempo para pagamento expirou. Tente novamente.');
        } else if (pinpadPaymentError === 'autodefRun') {
          toast.error(
            'Autodef não está rodando ou a maquininha não está corretamente conectada. Comunique o suporte.'
          );
        } else {
          toast.error('Erro ao pagar. Tente novamente mais tarde.');
        }
      }
    }
  }, [pinpadPaymentError]);

  useEffect(() => {
    if (pinpadPaymentStatus === 'success' && pinpadHealthStatus === 'success') {
      handleGetBalconySession();

      const timeout = setTimeout(() => {
        handleResetPinpadOperation();
      }, 3000);

      return () => clearTimeout(timeout);
    }
  }, [pinpadPaymentStatus, pinpadHealthStatus]);

  const currentPaymentFlow = updateCurrentPaymentFlow(
    pinpadPaymentStatus,
    pinpadHealthStatus
  );

  return {
    handleSwitchPinpadCancelStatus,
    handleResetPinpadOperation,
    handleCancelPinpadPayment,
    handleChoosePayWithPinpad,
    handleStartPinpadPayment,
    handleGetBalconySession,
    savePaymentAsync,
    sessionRequestStatus,
    pinpadPaymentStatus,
    pinpadHealthStatus,
    currentPaymentFlow,
    isLoadingPayment,
    isLeavingPayment,
    isChoosePinpad,
    balconySession,
    currentTable,
  };
}
